import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type AriaAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type FormHTMLAttributes,
  type ReactNode,
  type LabelHTMLAttributes,
  type TextareaHTMLAttributes
} from "react";
import { Search } from "lucide-react";
import { cn } from "../../lib/cn";

interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  controlID?: string;
  label: string;
  helper?: ReactNode;
  error?: ReactNode;
  wide?: boolean;
}

export function FormLayout({ className, ...props }: FormHTMLAttributes<HTMLFormElement>) {
  return <form className={cn("ui-form-layout", className)} {...props} />;
}

export function FormActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("ui-form-actions", className)} {...props} />;
}

interface FieldContextValue {
  controlID: string;
  describedBy?: string;
  invalid?: true;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function Field({ controlID: controlIDProp, label, helper, error, wide, className, children, ...props }: FieldProps) {
  const generatedID = useId();
  const controlID = controlIDProp ?? `field-${generatedID}`;
  const hasHelper = helper !== undefined && helper !== null && helper !== false;
  const hasError = error !== undefined && error !== null && error !== false;
  const helperID = hasHelper && !hasError ? `${controlID}-helper` : undefined;
  const errorID = hasError ? `${controlID}-error` : undefined;
  const describedBy = [helperID, errorID].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider value={{ controlID, describedBy, invalid: hasError ? true : undefined }}>
      <div
        className={cn("ui-field", wide && "ui-field-wide", className)}
        data-invalid={hasError ? "true" : undefined}
        {...props}
      >
        <label htmlFor={controlID}>{label}</label>
        {children}
        {hasHelper && !hasError && <small id={helperID} className="ui-field-helper">{helper}</small>}
        {hasError && <small id={errorID} className="ui-field-error" role="alert">{error}</small>}
      </div>
    </FieldContext.Provider>
  );
}

export function FieldSet({ className, ...props }: HTMLAttributes<HTMLFieldSetElement>) {
  return <fieldset className={cn("ui-field-set", className)} {...props} />;
}

export function FieldLegend({ className, ...props }: HTMLAttributes<HTMLLegendElement>) {
  return <legend className={cn("ui-field-legend", className)} {...props} />;
}

export function FieldGroup({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("ui-field-group", className)} {...props} />;
}

export function FieldLabel({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("ui-field-label", className)} {...props} />;
}

export function FieldDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("ui-field-helper", className)} {...props} />;
}

export function FieldError({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("ui-field-error", className)} role="alert" {...props} />;
}

interface FieldControlProps {
  id?: string;
  required?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: AriaAttributes["aria-invalid"];
  "aria-required"?: AriaAttributes["aria-required"];
}

export function useFieldControlProps<T extends FieldControlProps>(props: T): T {
  const field = useContext(FieldContext);
  if (!field) return props;

  const describedBy = [props["aria-describedby"], field.describedBy].filter(Boolean).join(" ") || undefined;
  return {
    ...props,
    id: props.id ?? field.controlID,
    "aria-describedby": describedBy,
    "aria-invalid": props["aria-invalid"] ?? field.invalid,
    "aria-required": props["aria-required"] ?? (props.required ? true : undefined)
  };
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => {
    const controlProps = useFieldControlProps(props);
    return <input className={cn("ui-input", className)} ref={ref} {...controlProps} />;
  }
);

Input.displayName = "Input";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => {
    const controlProps = useFieldControlProps(props);
    return <textarea className={cn("ui-input ui-textarea", className)} ref={ref} {...controlProps} />;
  }
);

Textarea.displayName = "Textarea";

export const RangeInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type">>(
  ({ className, ...props }, ref) => {
    const controlProps = useFieldControlProps(props);
    return <input className={cn("ui-range-input", className)} type="range" ref={ref} {...controlProps} />;
  }
);

RangeInput.displayName = "RangeInput";

export const FileInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type">>(
  ({ className, ...props }, ref) => {
    const controlProps = useFieldControlProps(props);
    return <input className={cn("ui-file-input", className)} type="file" ref={ref} {...controlProps} />;
  }
);

FileInput.displayName = "FileInput";

export const RadioInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type">>(
  ({ className, ...props }, ref) => {
    const controlProps = useFieldControlProps(props);
    return <input className={cn("ui-radio-input", className)} type="radio" ref={ref} {...controlProps} />;
  }
);

RadioInput.displayName = "RadioInput";

interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
}

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  ({ className, label, ...props }, ref) => (
    <label className={cn("ui-checkbox-field", className)}>
      <input className="ui-checkbox-input" type="checkbox" ref={ref} {...props} />
      <span>{label}</span>
    </label>
  )
);

CheckboxField.displayName = "CheckboxField";

interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "type"> {
  label: string;
  onChange: (value: string) => void;
  value: string;
}

export function SearchField({ label, value, onChange, className, ...props }: SearchFieldProps) {
  return (
    <label className={cn("ui-search-field", className)}>
      <span className="sr-only">{label}</span>
      <Search size={18} aria-hidden="true" />
      <input
        className="ui-search-input"
        type="search"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
        spellCheck={false}
        {...props}
      />
    </label>
  );
}
